// Keys "weapon.Assault" and "mode.SND" of video.json, the raw value when there is no translation.
const getWeaponTrad = (weapon: string, t: any) => {
  const key = `weapon.${weapon}`;
  const tmp = t(key);
  if (!tmp || tmp === key) {
    return weapon;
  }
  return tmp;
};

const getModeTrad = (mode: string, t: any) => {
  const key = `mode.${mode}`;
  const tmp = t(key);
  if (!tmp || tmp === key) {
    return mode;
  }
  return tmp;
};

const getFormatViews = (value: number) => {
  if (!value) {
    return '0';
  }
  const str = value.toString();
  if (str.length === 5 || str.length === 6) {
    return `${str.slice(0, str.length - 3)}K`;
  }
  if (str.length === 7) {
    return `${str[0]},${str[1]}M`;
  }
  if (str.length === 8) {
    return `${str[0]}${str[1]},${str[2]}M`;
  }
  if (str.length === 9) {
    return `${str[0]}${str[1]}Md`;
  }
  return str;
};

// Same as the app: more than 6 kills is shown as the infinity symbol.
const getKillTrad = (kills: number) => {
  if (kills > 6) {
    return '∞';
  }
  if (kills > 0) {
    return kills.toString();
  }
  return '';
};

const TIME_UNITS = [
  { key: 'year', seconds: 365 * 24 * 3600 },
  { key: 'month', seconds: 30 * 24 * 3600 },
  { key: 'day', seconds: 24 * 3600 },
  { key: 'hour', seconds: 3600 },
  { key: 'min', seconds: 60 },
];

// "il y a 5 min" / "5 min ago".
const getTimeAgo = (createdAt: number, t: any) => {
  const seconds = Math.floor((Date.now() - createdAt) / 1000);
  for (let i = 0; i < TIME_UNITS.length; i += 1) {
    const count = Math.floor(seconds / TIME_UNITS[i].seconds);
    if (count >= 1) {
      return t('admin.time.ago', { time: t(`admin.time.${TIME_UNITS[i].key}`, { count }) });
    }
  }
  return t('admin.time.now');
};

// A delay: "12 min", "3 h 10", "2 j 4 h" ("—" when unknown).
const getFormatDuration = (ms: number | null | undefined, t: any) => {
  if (ms === undefined || ms === null) {
    return '—';
  }
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) {
    return t('admin.time.duration.lessMin');
  }
  if (minutes < 60) {
    return t('admin.time.duration.min', { m: minutes });
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const m = minutes % 60;
    if (!m) {
      return t('admin.time.duration.hoursOnly', { h: hours });
    }
    return t('admin.time.duration.hours', { h: hours, m: String(m).padStart(2, '0') });
  }
  const days = Math.floor(hours / 24);
  const h = hours % 24;
  if (!h) {
    return t('admin.time.duration.daysOnly', { d: days });
  }
  return t('admin.time.duration.days', { d: days, h });
};

// Figures of the dashboard cards: "—" until they arrive, "1 234" / "8,1 %" in the language.
const getFormatNumber = (language: string, value?: number | null) => {
  if (value === undefined || value === null) {
    return '—';
  }
  return value.toLocaleString(language);
};

const getFormatPercent = (language: string, value?: number | null) => {
  if (value === undefined || value === null) {
    return '—';
  }
  return value.toLocaleString(language, { style: 'percent', maximumFractionDigits: 1 });
};

// "2026-10-11" (Paris day) to a short day label: "11/10" in French, "10/11" in English.
const getFormatDay = (language: string, date: string) => {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12)).toLocaleDateString(language, {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  });
};

export {
  getFormatDay,
  getFormatDuration,
  getFormatNumber,
  getFormatPercent,
  getKillTrad,
  getTimeAgo,
  getFormatViews,
  getModeTrad,
  getWeaponTrad,
};
